import SweepIcon from "@mui/icons-material/DeleteSweep";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { useAppNavigate } from "Infrastructure/Routing/useAppNavigate";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useDismissAllMutation } from "Notifications/Api/Mutations/useDismissAllMutation";
import { useDismissMutation } from "Notifications/Api/Mutations/useDismissMutation";
import { useMarkAllAsReadMutation } from "Notifications/Api/Mutations/useMarkAllAsReadMutation";
import { useMarkAsReadMutation } from "Notifications/Api/Mutations/useMarkAsReadMutation";
import { useMarkAsSeenMutation } from "Notifications/Api/Mutations/useMarkAsSeenMutation";
import type { UserNotificationDto } from "Notifications/Api/notificationApi";
import { useNotificationsQuery } from "Notifications/Api/Queries/useNotificationsQuery";
import { resolveNotificationRoute } from "Notifications/Utils/notificationRouteMap";
import { NotificationItem } from "./NotificationItem";

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationPanel = ({
  isOpen,
  onClose,
}: NotificationPanelProps): React.ReactNode => {
  const { t } = useResource();
  const navigate = useAppNavigate();
  const { data: notifications } = useNotificationsQuery();
  const markAsSeenMutation = useMarkAsSeenMutation();
  const markAsReadMutation = useMarkAsReadMutation();
  const markAllAsReadMutation = useMarkAllAsReadMutation();
  const dismissMutation = useDismissMutation();
  const dismissAllMutation = useDismissAllMutation();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (isOpen) {
      markAsSeenMutation.mutate(undefined);
    }
  }, [isOpen, markAsSeenMutation.mutate]);

  const handlePress = (notification: UserNotificationDto) => {
    if (!notification.readAt) {
      markAsReadMutation.mutate(notification.userNotificationID);
    }
    const route = resolveNotificationRoute(notification);
    if (route) {
      navigate(route);
    }
    onClose();
  };

  const handleDismiss = (userNotificationID: string) => {
    dismissMutation.mutate(userNotificationID);
  };

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) =>
    setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const handleMarkAll = () => {
    closeMenu();
    markAllAsReadMutation.mutate(undefined);
  };

  const handleDismissAll = () => {
    closeMenu();
    dismissAllMutation.mutate(undefined);
  };

  const items = notifications?.items ?? [];

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      slotProps={{
        paper: {
          sx: {
            width: 420,
            maxWidth: "100vw",
            backgroundColor: "background.default",
          },
        },
      }}
    >
      <Box
        sx={{
          p: Spacing.M,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight={600}>
            {t(Resources.Notifications.Title)}
          </Typography>
          <IconButton
            size="small"
            aria-label={t(Resources.Notifications.Actions)}
            onClick={openMenu}
            sx={{ color: "text.secondary" }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ zIndex: 1500 }}
          >
            <MenuItem
              onClick={handleMarkAll}
              disabled={markAllAsReadMutation.isPending || items.length === 0}
            >
              <ListItemIcon>
                <DoneAllIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t(Resources.Notifications.MarkAllAsRead)}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleDismissAll}
              disabled={dismissAllMutation.isPending || items.length === 0}
            >
              <ListItemIcon>
                <SweepIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {t(Resources.Notifications.DismissAll)}
              </ListItemText>
            </MenuItem>
          </Menu>
        </Stack>
      </Box>
      <Box sx={{ overflow: "auto", flex: 1 }}>
        {items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: Spacing.XXL,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t(Resources.Notifications.Empty)}
            </Typography>
          </Box>
        ) : (
          items.map((notification) => (
            <NotificationItem
              key={notification.userNotificationID}
              notification={notification}
              onPress={handlePress}
              onDismiss={handleDismiss}
            />
          ))
        )}
      </Box>
    </Drawer>
  );
};
